import React from 'react';
import { Box, useTheme, Typography, Button } from "@mui/material";
import { tokens } from "../../../theme";
import Modal from '@mui/material/Modal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import Checkbox from '@mui/material/Checkbox';
import { getAuthHeader } from '../../../utils/authToken';

function BulkDeleteModulesModal({
    eventModules,
    userRole,
    setBackdropOpen,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
}) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        overflowY: 'auto',
        maxHeight: '80vh',
        borderRadius: '8px',
    };

    // State for modal visibility
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setSelectedModules([]);
        setOpen(false);
    };

    const [selectedModules, setSelectedModules] = React.useState([]);

    const handleDelete = async () => {
        if (userRole !== "admin") {
            setErrorAlertMessage("You do not have permission to delete modules.");
            setErrorAlertOpen(true);
            return;
        }

        if (selectedModules.length === 0) {
            setErrorAlertMessage("Please select at least one module to delete.");
            setErrorAlertOpen(true);
            return;
        }
        
        setBackdropOpen(true);

        try {
            // Use batch delete endpoint - single request instead of N requests
            const response = await fetch(`${process.env.REACT_APP_URL}/api/v1/events/batch-delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getAuthHeader(),
                },
                body: JSON.stringify({ event_ids: selectedModules }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Error deleting modules: ${response.statusText}`);
            }

            setSuccessAlertMessage(data.message || "Selected modules have been deleted successfully.");
            setSuccessAlertOpen(true);
            setSelectedModules([]);
            handleClose();
            window.location.reload();
        } catch (error) {
            console.error("Error deleting modules:", error);
            setErrorAlertMessage(error.message);
            setErrorAlertOpen(true);
        } finally {
            setBackdropOpen(false);
        }
    }

    return (
        <>
            <Button
                variant="contained"
                sx={{
                    display: userRole === "admin" ? "block" : "none",
                    backgroundColor: colors.redAccent[400],
                    color: colors.primary[800],
                    "&:hover": {
                        backgroundColor: colors.redAccent[300],
                    },
                    width: "fit-content",
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                }}
                onClick={handleOpen}
            >
                Bulk Delete Modules
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Typography id="modal-modal-description" variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Select the modules you want to delete. This action cannot be undone.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {/* Here you can add checkboxes or a list of modules to select for deletion */}
                        {eventModules === null || eventModules.length === 0 ? (
                            <Box 
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    minWidth: "20rem",
                                    minHeight: "16rem",
                                    gap: "0.4rem",
                                }}
                            >
                                <InfoOutlinedIcon sx={{ my: "auto" }} />
                                <Typography variant="h4" color={colors.primary[450]} my="auto">
                                    No modules yet!
                                </Typography>
                            </Box>
                        ) : (
                            <>
                            {eventModules.map((eventModule, index) => { 
                                return (
                                    <Box 
                                        key={index} 
                                        sx={{ 
                                            mb: 1, 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            border: '1px solid',
                                            borderColor: colors.primary[100],
                                            backgroundColor: colors.primary[700],
                                            padding: '0.5rem',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedModules.includes(eventModule.id)}
                                            onChange={() => {
                                                setSelectedModules(prev => 
                                                    prev.includes(eventModule.id) 
                                                        ? prev.filter(id => id !== eventModule.id) 
                                                        : [...prev, eventModule.id]
                                                );
                                            }}
                                            color="primary"
                                            sx={{ mr: 2 }}
                                        />
                                        <Typography
                                            color={eventModule.is_canceled ? "error" : "primary"}
                                            sx={{ fontWeight: 'bold', mr: 2 }}
                                        >
                                            {eventModule.title}{eventModule.is_make_up_event && ' (Make-up)'}{userRole === "admin" && eventModule.zoom !== null && ` (Zoom ${eventModule.zoom.room_number})`}
                                        </Typography>
                                        <Typography sx={{ ml: "auto", mr: "1rem" }}>
                                            {dayjs(eventModule.start).format('MMM D, YYYY h:mm A')} - {dayjs(eventModule.end).format('h:mm A')}
                                        </Typography>
                                    </Box>
                                )
                            })}</>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        sx={{ 
                            mt: 2,
                            mr: "auto",
                            ml: "auto",
                            width: "fit-content",
                        }}
                    >
                        Delete
                    </Button>
                </Box>
            </Modal>
        </>
    )
}

export default BulkDeleteModulesModal